import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  generateFakeEmailData,
  EmailCampaign,
} from "../../../../utils/fakeEmailData";

// Define column keys
type ColumnKey =
  | "subject"
  | "emailType"
  | "sentDate"
  | "recipientCount"
  | "openRate"
  | "clickRate"
  | "status";

// Define sort directions
type SortDirection = "asc" | "desc";

const ManageEmails = () => {
  // State for email campaigns data
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);

  // State for search query
  const [searchQuery, setSearchQuery] = useState("");

  // State for sorting
  const [sortColumn, setSortColumn] = useState<ColumnKey>("sentDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Load fake data on component mount
  useEffect(() => {
    const data = generateFakeEmailData(30);
    setCampaigns(data);
  }, []);

  // Handle search
  const handleSearchChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(target.value);
  };

  // Handle sort
  const handleRequestSort = (column: ColumnKey) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortColumn(column);
  };

  // Filter campaigns based on search query
  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Function to get status color
  const getStatusColor = (status: EmailCampaign["status"]) => {
    switch (status) {
      case "sent":
        return "success";
      case "draft":
        return "default";
      case "scheduled":
        return "warning";
      default:
        return "default";
    }
  };

  // Function to get email type display name
  const getEmailTypeDisplay = (type: EmailCampaign["emailType"]) => {
    switch (type) {
      case "newsletter":
        return "Newsletter";
      case "press_release":
        return "Press Release";
      case "bulletin":
        return "Bulletin";
      case "eblast":
        return "E-Blast";
      default:
        return type;
    }
  };

  // Sort campaigns based on current sort column and direction
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    // Always show drafts and scheduled emails first
    if (a.status === "draft" && b.status !== "draft") return -1;
    if (a.status !== "draft" && b.status === "draft") return 1;
    if (a.status === "scheduled" && b.status === "sent") return -1;
    if (a.status === "sent" && b.status === "scheduled") return 1;

    // If both campaigns have the same status, sort by the selected column
    if (sortColumn === "subject") {
      return sortDirection === "asc"
        ? a.subject.localeCompare(b.subject)
        : b.subject.localeCompare(a.subject);
    }

    if (sortColumn === "emailType") {
      return sortDirection === "asc"
        ? a.emailType.localeCompare(b.emailType)
        : b.emailType.localeCompare(a.emailType);
    }

    if (sortColumn === "sentDate") {
      // Handle empty dates (drafts)
      if (!a.sentDate) return sortDirection === "asc" ? -1 : 1;
      if (!b.sentDate) return sortDirection === "asc" ? 1 : -1;

      // Compare dates
      return sortDirection === "asc"
        ? new Date(a.sentDate).getTime() - new Date(b.sentDate).getTime()
        : new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime();
    }

    if (sortColumn === "recipientCount") {
      return sortDirection === "asc"
        ? a.recipientCount - b.recipientCount
        : b.recipientCount - a.recipientCount;
    }

    if (sortColumn === "openRate") {
      return sortDirection === "asc"
        ? a.openRate - b.openRate
        : b.openRate - a.openRate;
    }

    if (sortColumn === "clickRate") {
      return sortDirection === "asc"
        ? a.clickRate - b.clickRate
        : b.clickRate - a.clickRate;
    }

    if (sortColumn === "status") {
      return sortDirection === "asc"
        ? a.status.localeCompare(b.status)
        : b.status.localeCompare(a.status);
    }

    return 0;
  });

  return (
    <div className="p-6">
      {/* Header and search */}
      <div className="mb-6 max-w-lg">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search Previous Emails"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="text-gray-500" />
              </InputAdornment>
            ),
          }}
          className="bg-white"
        />
      </div>

      {/* Emails table */}
      <TableContainer component={Paper} className="mb-8">
        <Table aria-label="email campaigns table">
          <TableHead className="bg-gray-50">
            <TableRow>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "subject"}
                  direction={sortColumn === "subject" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("subject")}
                >
                  Subject
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "emailType"}
                  direction={sortColumn === "emailType" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("emailType")}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "sentDate"}
                  direction={sortColumn === "sentDate" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("sentDate")}
                >
                  Sent Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "recipientCount"}
                  direction={
                    sortColumn === "recipientCount" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("recipientCount")}
                >
                  Recipients
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "openRate"}
                  direction={sortColumn === "openRate" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("openRate")}
                >
                  Open Rate
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "clickRate"}
                  direction={sortColumn === "clickRate" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("clickRate")}
                >
                  Click Rate
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  style={{ fontWeight: "bold" }}
                  active={sortColumn === "status"}
                  direction={sortColumn === "status" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCampaigns.map((campaign) => (
              <TableRow
                key={campaign.id}
                className={`hover:bg-gray-50 transition-colors ${
                  campaign.status === "draft"
                    ? "bg-gray-50"
                    : campaign.status === "scheduled"
                    ? "bg-blue-50"
                    : ""
                }`}
              >
                <TableCell component="th" scope="row">
                  <div className="font-medium">{campaign.subject}</div>
                  <div className="text-xs text-gray-500">
                    From: {campaign.fromName}
                  </div>
                </TableCell>
                <TableCell>{getEmailTypeDisplay(campaign.emailType)}</TableCell>
                <TableCell>
                  {campaign.sentDate ||
                    (campaign.status === "draft" ? "Not sent" : "Scheduled")}
                </TableCell>
                <TableCell>
                  {campaign.recipientCount.toLocaleString()}
                </TableCell>
                <TableCell>
                  {campaign.status === "sent" ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "50px",
                          height: "6px",
                          backgroundColor: "#E0E0E0",
                          borderRadius: "3px",
                          marginRight: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: `${campaign.openRate}%`,
                            height: "100%",
                            backgroundColor:
                              campaign.openRate > 30
                                ? "#4CAF50"
                                : campaign.openRate > 20
                                ? "#FFC107"
                                : "#F44336",
                            borderRadius: "3px",
                          }}
                        />
                      </div>
                      {campaign.openRate}%
                    </Box>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {campaign.status === "sent" ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "50px",
                          height: "6px",
                          backgroundColor: "#E0E0E0",
                          borderRadius: "3px",
                          marginRight: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: `${campaign.clickRate * 3}%`, // Multiply by 3 to make it more visible
                            height: "100%",
                            backgroundColor:
                              campaign.clickRate > 10
                                ? "#4CAF50"
                                : campaign.clickRate > 5
                                ? "#FFC107"
                                : "#F44336",
                            borderRadius: "3px",
                          }}
                        />
                      </div>
                      {campaign.clickRate}%
                    </Box>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      campaign.status === "sent"
                        ? "Sent"
                        : campaign.status === "draft"
                        ? "Draft"
                        : "Scheduled"
                    }
                    color={getStatusColor(campaign.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex space-x-1">
                    <Tooltip title="View">
                      <IconButton size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {campaign.status === "draft" && (
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Duplicate">
                      <IconButton size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ManageEmails;
